import { Component, EventEmitter, Input, Output, Inject, AfterViewInit } from '@angular/core'
import { GenericLayer } from '../../../shared/model/layers/generic-layer'
import { BaseLayer } from '../../../shared/model/layers/base-layer'
import { Dialog } from '@angular/cdk/dialog'
import {
    RenameLayerDialogComponent,
    RenameLayerDialogData,
} from './rename-layer-dialog/rename-layer-dialog.component'
import { Store } from '@ngrx/store'
import { viewerActions } from '../../../state/viewer/viewer.actions'
import { GenericConfirmationDialogModel } from 'src/app/shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { GenericConfirmationDialogComponent } from 'src/app/shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { DatasetApiService } from 'src/app/shared/services/dataset-api.service'
import { ToasterService } from 'src/app/shared/services/toaster.service'
import { takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { selectionActions } from 'src/app/state/selection/selection.actions'
import { SelectionLayer } from 'src/app/shared/model/layers/selection-layer'
import { topLevelSelectionName } from 'src/app/state/selection/selection.reducer'
import { ImageInvlerpParameters } from 'neuroglancer/webgl/shader_ui_controls'
import { DataTypeInterval } from 'neuroglancer/util/lerp'
import { DOCUMENT } from '@angular/common'
import { SelectionType } from 'src/app/state/selection/selection.state'
import { LayerType } from 'src/app/shared/model/layers/layer-types'

@Component({
    selector: 'app-single-layer-detail',
    templateUrl: './single-layer-detail.component.html',
    styleUrls: ['./single-layer-detail.component.scss'],
})
export class SingleLayerDetailComponent implements AfterViewInit {
    @Input()
    layer: GenericLayer | SelectionLayer | undefined | null

    @Input()
    colorPalette: string[]

    @Output()
    close = new EventEmitter<void>()

    @Output()
    layerRenamed = new EventEmitter<{ oldName: string; newName: string }>()

    private readonly destroy$ = new Subject<void>()

    protected readonly topLevelSelectionName = topLevelSelectionName
    histogramData: number[] = []
    computedHistogramData: number[] = []
    fixedNormalizeRange = [1, 255]
    normalizeRange: number[] = []

    get histogramMaxValue(): number {
        return this.fixedNormalizeRange[1] || 255
    }

    get histogramMinValue(): number {
        return this.fixedNormalizeRange[0] || 1
    }

    constructor(
        private dialog: Dialog,
        private store: Store,
        private datasetApiService: DatasetApiService,
        private toasterService: ToasterService,
        @Inject(DOCUMENT) private readonly document: Document
    ) {}

    ngAfterViewInit(): void {
        if (!this.layer?.source) return
        if (this.layer?.normalize) {
            this.layer.normalize.value$.pipe(takeUntil(this.destroy$)).subscribe(normalizeValue => {
                if (normalizeValue && normalizeValue.range) {
                    const range = [Number(normalizeValue.range[0]), Number(normalizeValue.range[1])]
                    if (range[0] !== undefined && range[1] !== undefined) {
                        this.normalizeRange = range
                    }
                }
            })
        } else {
            this.normalizeRange = [1, 255]
        }

        this.datasetApiService
            .getHistogram(this.layer.source.split('precomputed://')[1])
            .subscribe({
                next: data => {
                    if (data.length > 0) {
                        const canvas = this.document.getElementById(
                            'canvas-histogram'
                        ) as unknown as HTMLCanvasElement
                        canvas.style.height = '80px'

                        const autoComputeBtns = this.document.querySelectorAll(
                            'button[class*="icon-button"][id="autoComputeBtn"]'
                        ) as NodeListOf<HTMLButtonElement>
                        autoComputeBtns.forEach(btn => {
                            btn.style.visibility = 'visible'
                        })
                        const zeroValueRemoved = data.splice(1, data.length - 1)

                        this.histogramData = zeroValueRemoved
                        let accumulatedSum = 0
                        this.computedHistogramData = zeroValueRemoved.map(value => {
                            accumulatedSum += value
                            return accumulatedSum
                        })
                        this.drawHistogram()
                    }
                },
                error: error => {
                    console.error('Error fetching histogram:', error)
                },
            })
    }

    private drawHistogram(): void {
        const data = this.computedHistogramData
        if (!data || data.length === 0 || !data.find(value => value > 0)) {
            return
        }

        const canvas = this.document.getElementById(
            'canvas-histogram'
        ) as unknown as HTMLCanvasElement
        const ctx = canvas?.getContext('2d')

        if (!canvas || !ctx) {
            console.error('Canvas or context not available')
            return
        }

        const width = canvas.width
        const height = canvas.height
        const barWidth = width / data.length

        const maxValue = Math.max(...data)

        // Clear canvas
        ctx.clearRect(0, 0, width, height)

        // Draw bars
        data.forEach((value, i) => {
            const barHeight = (value / maxValue) * (height - 20)
            const x = i * barWidth
            const y = height - barHeight

            if (this.normalizeRange[1] > this.normalizeRange[0]) {
                if (
                    i >= this.normalizeRange[0] - this.fixedNormalizeRange[0] &&
                    i < this.normalizeRange[1] + 1 - this.fixedNormalizeRange[0]
                ) {
                    ctx.fillStyle = '#EA514F'
                } else {
                    ctx.fillStyle = '#aaaaaa'
                }
            } else {
                if (
                    i >= this.normalizeRange[1] + 1 - this.fixedNormalizeRange[0] &&
                    i < this.normalizeRange[0] - this.fixedNormalizeRange[0]
                ) {
                    ctx.fillStyle = '#555555'
                } else {
                    ctx.fillStyle = '#aaaaaa'
                }
            }

            ctx.fillRect(x, y, Math.ceil(barWidth), barHeight)
        })
    }

    get hasSliderControls(): boolean {
        return (
            !!this.layer?.opacityPercent ||
            !!this.layer?.opacityUnselectedPercent ||
            !!this.layer?.saturationPercent ||
            !!this.layer?.brightness ||
            !!this.layer?.contrast ||
            !!this.layer?.normalize
        )
    }

    protected setSegmentationColorMode(segmentationColorMode: SegmentationColorMode): void {
        if (!this.layer || !this.layer.segmentationColor) {
            return
        }
        if (segmentationColorMode == SegmentationColorMode.RandomSeed) {
            this.layer.segmentationColor?.setValue(null)
        } else {
            this.layer.segmentationColor?.setValue('#ff0000')
        }
    }

    segmentationColorMode(segmentationColor: string | null): SegmentationColorMode {
        if (segmentationColor) {
            return SegmentationColorMode.FixedColor
        } else {
            return SegmentationColorMode.RandomSeed
        }
    }

    protected readonly SegmentationColorModeEnum = SegmentationColorMode

    resetNormalize(): void {
        let accumulatedSum = 0
        this.computedHistogramData = this.histogramData.map(value => {
            accumulatedSum += value
            return accumulatedSum
        })
        this.normalizeRange = [1, 255]
        this.fixedNormalizeRange = [1, 255]
        this.setNormalizeRange(1, 255)
    }

    setNormalizeRange(lower: number | null, upper: number | null): void {
        var lowerNumber: number | boolean | bigint = 1
        var upperNumber: number | boolean | bigint = 1
        var window: DataTypeInterval = [0, 0]
        var channel: number[] = []

        this.layer?.normalize?.value$.subscribe(normalizeValue => {
            if (normalizeValue) {
                lowerNumber = lower ?? normalizeValue.range[0]
                upperNumber = upper ?? normalizeValue.range[1]
                window = normalizeValue.window
                channel = normalizeValue.channel
            }
        })

        if (typeof lowerNumber === 'number' && lowerNumber < 1) {
            lowerNumber = 1
        }

        this.layer?.normalize?.setValue({
            range: [lowerNumber, upperNumber],
            window: window,
            channel: channel,
        } as ImageInvlerpParameters)

        this.normalizeRange = [lowerNumber, upperNumber]
        this.drawHistogram()
    }

    autoComputeRange(percentile: number = 1): void {
        if (!this.histogramData || this.histogramData.length === 0) {
            return
        }

        let sum = 0
        const sortedValues = [...this.histogramData].map(value => (sum += value))
        const sumPercentile = (sum * percentile) / 100

        const lowerPercentileIndex = Math.max(
            1,
            sortedValues.findIndex(value => value >= sumPercentile)
        )
        const upperPercentileIndex =
            this.histogramData.length -
            1 -
            sortedValues
                .slice()
                .reverse()
                .findIndex(value => value <= sum - sumPercentile)

        let accumulatedSum = 0
        this.computedHistogramData = this.histogramData.map(value => {
            accumulatedSum += value
            return accumulatedSum
        })

        this.setNormalizeRange(lowerPercentileIndex, upperPercentileIndex)
    }

    openRenameDialog(): void {
        if (!this.layer || !this.layer.name) {
            console.warn('Cannot rename layer: layer or layer name is missing')
            return
        }
        const currentName = this.layer.nameOrDefault

        const dialogData: RenameLayerDialogData = {
            currentName: currentName,
            layer: this.layer as unknown as BaseLayer,
            selectionType:
                (this.layer as SelectionLayer)?.['_selection']?.type ?? SelectionType.Free,
        }

        const dialogRef = this.dialog.open<string>(RenameLayerDialogComponent, {
            data: dialogData,
            autoFocus: 'first-tabbable',
        })

        dialogRef.closed.subscribe(newName => {
            if (newName) {
                this.layerRenamed.emit({
                    oldName: this.layer!.nameOrDefault,
                    newName,
                })
            }
        })
    }

    openDeleteDialog(): void {
        if (this.layer?.type !== LayerType.Selection) {
            this.deleteLayer()
        } else if (!(this.layer as SelectionLayer).groupName) {
            this.deleteFreeSelection()
        } else {
            this.deleteHierarchicalSelection((this.layer as SelectionLayer)?.['_selection'].type)
        }
    }

    deleteLayer(): void {
        const layerName = this.layer!.nameOrDefault
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this layer?',
            item: layerName,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.datasetApiService.deleteLayer(layerName).subscribe({
                error: error => {
                    this.toasterService.httpError(error, 'Failed to delete layer')
                },
            })
            this.store.dispatch(viewerActions.deleteLayer({ name: layerName }))
            this.toasterService.success(layerName, 'Layer deleted successfully')
            this.close.emit()
        })
    }

    deleteFreeSelection(): void {
        const selectionName = this.layer!.nameOrDefault
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this selection?',
            item: selectionName,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(selectionActions.deleteFreeSelection({ name: selectionName! }))
            this.toasterService.success(selectionName, 'Selection deleted successfully')
            this.close.emit()
        })
    }

    deleteHierarchicalSelection(selectionType: SelectionType): void {
        const selectionName = this.layer!.nameOrDefault
        const data: GenericConfirmationDialogModel = {
            title: `Are you sure you want to delete this ${
                selectionType === SelectionType.Hierarchical ? 'hierarchical' : 'unsupervised'
            } selection?`,
            item: selectionName + ' (Recursively)',
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(
                selectionType === SelectionType.Hierarchical
                    ? selectionActions.deleteHierarchicalSelectionNodeRecursively({
                          groupName: (this.layer as SelectionLayer).groupName!,
                          name: selectionName,
                      })
                    : selectionActions.deleteAutoCellTypeDetectionNodeRecursively({
                          groupName: (this.layer as SelectionLayer).groupName!,
                          name: selectionName,
                      })
            )
            this.toasterService.success(selectionName, 'Selection deleted successfully')
            this.close.emit()
        })
    }
}

export enum SegmentationColorMode {
    RandomSeed = 'random',
    FixedColor = 'fixed',
}
